/**
 * SendLocationDialog — Enviar LOCALIZAÇÃO na conversa.
 *
 * Mapa interativo Leaflet + OpenStreetMap (decisão do dono, spec 2026-08-19):
 * pino arrastável, clique no mapa reposiciona, busca de endereço via Nominatim
 * (debounce 600ms — respeita o rate limit do serviço público) e botão "usar
 * minha localização" (geolocation do navegador). Envia
 * `content: [{ type: 'location', latitude, longitude, name?, address? }]`
 * pelo POST /send existente.
 *
 * Modal shared = tema claro fixo (regra da casa p/ modais/drawers).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import type { MessageContent } from '@troia-v3/types';
import { Button, Input } from '@/shared/components/ui';
import { Modal, ModalHeader, ModalFooter } from '@/shared/components/ui/Modal';
import { Icon } from '@/shared/icons';

// Fix clássico do Leaflet + bundlers (Vite): os PNGs do marker default não
// resolvem sozinhos — apontar explicitamente pros assets empacotados.
const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

/** Centro default: Brasil (visão país) quando ainda não há posição. */
const DEFAULT_CENTER: [number, number] = [-14.235, -51.9253];
const DEFAULT_ZOOM = 4;
const PICKED_ZOOM = 16;

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
}

interface SendLocationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (content: MessageContent[]) => void;
  isLoading?: boolean;
}

/** Clique no mapa reposiciona o pino. */
function ClickToPlace({ onPick }: { onPick: (lat: number, lng: number) => void }): null {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
}

/**
 * Recentra o mapa APENAS quando a posição vem de busca/geolocalização
 * (`flyTarget`). Clique no mapa e arrasto do pino NÃO recentram — recentrar
 * ali destruía o zoom escolhido pelo usuário (achado da revisão adversarial).
 */
function FlyTo({ flyTarget }: { flyTarget: [number, number] | null }): null {
  const map = useMap();
  useEffect(() => {
    if (flyTarget) map.flyTo(flyTarget, PICKED_ZOOM, { duration: 0.6 });
  }, [map, flyTarget]);
  return null;
}

const SendLocationDialog: React.FC<SendLocationDialogProps> = ({
  isOpen,
  onClose,
  onSend,
  isLoading = false,
}) => {
  const [position, setPosition] = useState<[number, number] | null>(null);
  /** Alvo de recentragem — setado SÓ por busca/geolocalização (nunca por clique/arrasto). */
  const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);
  const [locationName, setLocationName] = useState('');
  const [address, setAddress] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [geoError, setGeoError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset-on-close (achado da revisão adversarial 2026-08-19): o ChatBox monta
  // este dialog INCONDICIONALMENTE e o fechamento pós-envio seta só o isOpen
  // do pai — sem este effect, reabrir mostrava o pino/nome/endereço do envio
  // anterior com o botão habilitado (risco de reenviar a localização stale).
  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      setFlyTarget(null);
      setLocationName('');
      setAddress('');
      setSearchTerm('');
      setResults([]);
      setGeoError('');
    }
  }, [isOpen]);

  // Busca Nominatim com debounce — política de uso do serviço público
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = searchTerm.trim();
    if (term.length < 3) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      void (async () => {
        try {
          setSearching(true);
          const url = `https://nominatim.openstreetmap.org/search?format=json&limit=5&accept-language=pt-BR&q=${encodeURIComponent(term)}`;
          const res = await fetch(url);
          const data = (await res.json()) as NominatimResult[];
          setResults(Array.isArray(data) ? data : []);
        } catch {
          setResults([]);
        } finally {
          setSearching(false);
        }
      })();
    }, 600);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  const pickResult = (r: NominatimResult) => {
    const lat = parseFloat(r.lat);
    const lng = parseFloat(r.lon);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setPosition([lat, lng]);
      setFlyTarget([lat, lng]);
      setAddress(r.display_name);
      setResults([]);
      setSearchTerm('');
    }
  };

  const useMyLocation = () => {
    setGeoError('');
    if (!navigator.geolocation) {
      setGeoError('Geolocalização não suportada neste navegador');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition([pos.coords.latitude, pos.coords.longitude]);
        setFlyTarget([pos.coords.latitude, pos.coords.longitude]);
      },
      () => setGeoError('Não foi possível obter sua localização — verifique a permissão do navegador'),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  const handleSend = () => {
    if (!position) return;
    const [latitude, longitude] = position;
    const loc: MessageContent = {
      type: 'location',
      latitude,
      longitude,
      ...(locationName.trim() ? { name: locationName.trim() } : {}),
      ...(address.trim() ? { address: address.trim() } : {}),
    };
    onSend([loc]);
  };

  const handleClose = () => {
    setPosition(null);
    setFlyTarget(null);
    setLocationName('');
    setAddress('');
    setSearchTerm('');
    setResults([]);
    setGeoError('');
    onClose();
  };

  const coordsLabel = useMemo(
    () => (position ? `${position[0].toFixed(6)}, ${position[1].toFixed(6)}` : 'clique no mapa, busque um endereço ou use sua localização'),
    [position],
  );

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="lg" closeOnEscape={!isLoading}>
      <div data-testid="send-location-dialog">
        <ModalHeader title="Enviar localização" onClose={isLoading ? undefined : handleClose} />

        {/* Busca de endereço + minha localização */}
        <div className="px-5 pt-3 flex gap-2 items-start">
          <div className="relative flex-1 min-w-0">
            <Input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar endereço ou lugar…"
              icon="search"
              data-testid="send-location-search-input"
            />
            {(results.length > 0 || searching) && (
              <div
                className="absolute left-0 right-0 top-full mt-1 rounded-lg overflow-hidden z-[70]"
                style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)' }}
                data-testid="send-location-results"
              >
                {searching ? (
                  <div className="px-3 py-2 text-xs" style={{ color: '#94a3b8' }}>Buscando…</div>
                ) : (
                  results.map((r, i) => (
                    <button
                      key={`${r.lat}-${r.lon}-${i}`}
                      type="button"
                      onClick={() => pickResult(r)}
                      className="w-full text-left px-3 py-2.5 text-xs transition-colors hover:bg-slate-50"
                      style={{ color: '#334155', minHeight: 44 }}
                      data-testid={`send-location-result-${i}`}
                    >
                      <Icon name="map-pin" className="w-3.5 h-3.5 inline-block mr-1.5 align-[-2px]" style={{ color: '#8b5cf6' }} />
                      {r.display_name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <Button variant="secondary" onClick={useMyLocation} data-testid="send-location-use-mine" title="Usar minha localização">
            <Icon name="target" className="w-4 h-4" />
          </Button>
        </div>
        {geoError && (
          <div className="px-5 pt-1 text-xs" style={{ color: '#dc2626' }} data-testid="send-location-geo-error">{geoError}</div>
        )}

        {/* Mapa */}
        <div className="px-5 pt-3">
          <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #e5e7eb' }} data-testid="send-location-map">
            <MapContainer
              center={position ?? DEFAULT_CENTER}
              zoom={position ? PICKED_ZOOM : DEFAULT_ZOOM}
              style={{ height: 280, width: '100%' }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ClickToPlace onPick={(lat, lng) => { setPosition([lat, lng]); setResults([]); }} />
              <FlyTo flyTarget={flyTarget} />
              {position && (
                <Marker
                  position={position}
                  draggable
                  eventHandlers={{
                    dragend: (e) => {
                      const ll = (e.target as L.Marker).getLatLng();
                      setPosition([ll.lat, ll.lng]);
                    },
                  }}
                />
              )}
            </MapContainer>
          </div>
          <div className="pt-1.5 text-[11px]" style={{ color: '#94a3b8' }} data-testid="send-location-coords">
            📍 {coordsLabel}
          </div>
        </div>

        {/* Nome e endereço (opcionais, aparecem no balão do WhatsApp) */}
        <div className="px-5 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            placeholder="Nome do local (opcional)"
            data-testid="send-location-name-input"
          />
          <Input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Endereço (opcional)"
            data-testid="send-location-address-input"
          />
        </div>

        <ModalFooter>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading} data-testid="send-location-cancel">
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={!position || isLoading} data-testid="send-location-confirm">
            {isLoading ? 'Enviando…' : 'Enviar localização'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

export default SendLocationDialog;
